import { useState } from 'react';
import { SchreibtAls } from '@/components/ui/SchreibtAls';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useKommunikationsTypen } from '@/hooks/useKommunikationsTypen';
import { useAuth } from '@/hooks/useAuth';
import { useDraft } from '@/hooks/useDraft';
import { DraftHinweis } from '@/components/ui/DraftHinweis';
import { ErwaehnungTextarea } from '@/components/ui/ErwaehnungTextarea';
import type { KommunikationCreateRequest } from '@/types/detail';
import { FOKUS_RING } from '@/lib/fokus';

const ERGEBNISSE = [
  ['n_a', '—'],
  ['erreicht', 'erreicht'],
  ['mailbox', 'Mailbox'],
  ['kein_anschluss', 'kein Anschluss'],
  ['nicht_erreicht', 'nicht erreicht'],
  ['keine_antwort', 'keine Antwort']
] as const;

type Props = {
  bestellungId: number;
  /** Promise, damit erst nach bestaetigtem Erfolg geleert wird (S-1). */
  onSubmit: (req: Omit<KommunikationCreateRequest, 'bestellung_id'>) => Promise<void>;
  isPending: boolean;
};

type Entwurf = {
  typCode: string;
  richtung: 'eingehend' | 'ausgehend' | null;
  ergebnis: string | null;
  freitext: string;
};

const TYPEN_OHNE_RICHTUNG = new Set(['notiz', 'im_laden']);
const TYPEN_OHNE_ERGEBNIS = new Set(['notiz', 'im_laden']);

export function KommunikationFormular({ bestellungId, onSubmit, isPending }: Props) {
  const typen = useKommunikationsTypen();
  const auth = useAuth();
  const [typCode, setTypCode] = useState('anruf');
  const [richtung, setRichtung] = useState<'eingehend' | 'ausgehend' | null>('ausgehend');
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const [freitext, setFreitext] = useState('');

  const zeigeRichtung = !TYPEN_OHNE_RICHTUNG.has(typCode);
  const zeigeErgebnis = !TYPEN_OHNE_ERGEBNIS.has(typCode);

  // Entwurf je Bestellung sichern (S-1). Der Freitext ist hier das
  // eigentlich Schuetzenswerte; Typ und Richtung kommen mit, damit der
  // Eintrag vollstaendig zurueckkehrt.
  const draft = useDraft<Entwurf>({
    scope: `kommunikation-${bestellungId}`,
    userId: auth.data?.user.id ?? null,
    wert: { typCode, richtung, ergebnis, freitext },
    aktiv: true,
    istLeer: (w) => w.freitext.trim() === ''
  });

  async function abschicken(): Promise<void> {
    if (freitext.trim() === '') return;
    try {
      await onSubmit({
        typ_code: typCode,
        richtung: zeigeRichtung ? richtung : null,
        ergebnis: zeigeErgebnis ? ergebnis : null,
        freitext: freitext.trim()
      });
    } catch {
      // Fehlgeschlagen: Eingaben stehen lassen, der Entwurf bleibt
      // ebenfalls erhalten. Die Meldung kommt aus der Mutation.
      return;
    }
    draft.loeschen();
    setFreitext('');
    setErgebnis(null);
  }

  return (
    <div className="space-y-2 rounded-md border border-line bg-cream-warm p-3">
      {/* A9.1: Das Protokoll ist append-only — eine Falschzuordnung
          bleibt für immer stehen. Deshalb steht hier, unter wessen Namen
          der Eintrag gleich entsteht. */}
      <SchreibtAls text="Eintrag von" />

      {draft.angebot !== null && (
        <DraftHinweis
          text="ein begonnener Eintrag"
          onWiederherstellen={() => {
            const d = draft.annehmen();
            if (d === null) return;
            setTypCode(d.typCode);
            setRichtung(d.richtung);
            setErgebnis(d.ergebnis);
            setFreitext(d.freitext);
          }}
          onVerwerfen={draft.verwerfen}
        />
      )}
      <div className="flex flex-wrap items-end gap-2 text-xs text-ink-soft">
        <label className="flex flex-col gap-1">
          Typ
          <select
            value={typCode}
            onChange={(e) => setTypCode(e.target.value)}
            className="min-h-[44px] rounded border border-line bg-cream px-2 py-1 text-sm text-ink"
          >
            {typen.data?.items.map((t) => (
              <option key={t.code} value={t.code}>{t.label}</option>
            ))}
          </select>
        </label>

        {zeigeRichtung && (
          <div className="inline-flex overflow-hidden rounded-md border border-line">
            <button
              type="button"
              onClick={() => setRichtung('eingehend')}
              className={
                richtung === 'eingehend'
                  ? 'min-h-[44px] bg-akzent px-3 py-1.5 text-sm text-cream'
                  : 'min-h-[44px] px-3 py-1.5 text-sm text-ink-soft hover:bg-cream-dark'
              }
            >
              eingehend
            </button>
            <button
              type="button"
              onClick={() => setRichtung('ausgehend')}
              className={
                richtung === 'ausgehend'
                  ? 'min-h-[44px] bg-akzent px-3 py-1.5 text-sm text-cream'
                  : 'min-h-[44px] px-3 py-1.5 text-sm text-ink-soft hover:bg-cream-dark'
              }
            >
              ausgehend
            </button>
          </div>
        )}

        {zeigeErgebnis && (
          <label className="flex flex-col gap-1">
            Ergebnis
            <select
              value={ergebnis ?? ''}
              onChange={(e) => setErgebnis(e.target.value === '' ? null : e.target.value)}
              className="min-h-[44px] rounded border border-line bg-cream px-2 py-1 text-sm text-ink"
            >
              <option value="">— wählen —</option>
              {ERGEBNISSE.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* @ öffnet die Mitarbeiterliste. Solange sie offen steht, wählt
          Enter aus ihr aus statt abzuschicken — das regelt die Komponente
          selbst, hier bleibt nur der Strg+Enter-Griff. */}
      <ErwaehnungTextarea
        value={freitext}
        onChange={setFreitext}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void abschicken();
          }
        }}
        rows={3}
        placeholder="Was wurde besprochen? Mit @ jemanden erwähnen (Strg+Enter zum Senden)"
        className={`w-full rounded-md border border-line bg-cream px-3 py-2 text-sm ${FOKUS_RING}`}
      />

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={() => void abschicken()}
          disabled={isPending || freitext.trim() === ''}
        >
          <Send className="h-4 w-4" />
          Eintrag speichern
        </Button>
      </div>
    </div>
  );
}
